import { atom } from 'nanostores';
import { ConfigDatabase } from '../../engine/database';
import { upgradesData, type UpgradeConfig } from '../../generated/upgrades';
import { profileStore, profileActions } from '../../stores/game/profile';
import { itemsDB } from './ItemsDB';

export type UpgradeId = string;

/**
 * Интерфейс для стоимости апгрейда
 */
export interface UpgradeCost {
  coins: number;
  resources: Array<{
    itemId: string;
    amount: number;
  }>;
}

/**
 * Интерфейс для информации о покупке апгрейда
 */
export interface UpgradePurchaseInfo {
  upgradeId: UpgradeId;
  currentLevel: number;
  newLevel: number;
  cost: UpgradeCost;
  canAfford: boolean;
  isMaxLevel: boolean;
  isUnlocked: boolean;
}

/**
 * База данных для апгрейдов с менеджером покупок
 */
export class UpgradesDB extends ConfigDatabase<UpgradeConfig, string> {
  private playerLevel = atom<number>(1);

  constructor() {
    super({
      base: upgradesData,
      overrides: {}, // Пока без локальных правок
      getId: config => config.upg_id,
      validate: (config, id) => {
        if (config.max_lvl < 1) {
          throw new Error(`Upgrade ${id}: max_lvl must be >= 1`);
        }
        if (config.unlock_lvl < 1) {
          throw new Error(`Upgrade ${id}: unlock_lvl must be >= 1`);
        }
        if (config.base_cost_coin < 0) {
          throw new Error(`Upgrade ${id}: base_cost_coin must be >= 0`);
        }
      },
    });

    // Отложенная инициализация подписки
    this.initializePlayerLevelSubscription();
  }

  /**
   * Ленивая инициализация подписки на уровень игрока
   */
  private initializePlayerLevelSubscription(): void {
    // Не инициализируем сразу, подписка будет создана при первом обращении
  }

  /**
   * Настройка подписки на уровень игрока (ленивая)
   */
  private setupPlayerLevelSubscription(): void {
    if (this.playerLevelSubscription) return; // Уже настроена

    this.playerLevelSubscription = profileStore.subscribe(profile => {
      this.playerLevel.set(profile.gameData.level);
    });
  }

  private playerLevelSubscription: (() => void) | null = null;

  /**
   * Получить конфигурацию апгрейда
   */
  getUpgradeConfig(upgradeId: UpgradeId): UpgradeConfig | undefined {
    return this.get(upgradeId);
  }

  /**
   * Получить текущий уровень апгрейда
   */
  getUpgradeLevel(upgradeId: UpgradeId): number {
    this.setupPlayerLevelSubscription(); // Ленивая инициализация
    const profile = profileStore.get();
    return profile.upgrades[upgradeId] || 0;
  }

  /**
   * Получить все уровни апгрейдов
   */
  getAllUpgradeLevels(): Record<UpgradeId, number> {
    this.setupPlayerLevelSubscription(); // Ленивая инициализация
    return { ...profileStore.get().upgrades };
  }

  /**
   * Проверить, разблокирован ли апгрейд
   */
  isUpgradeUnlocked(upgradeId: UpgradeId): boolean {
    const config = this.getUpgradeConfig(upgradeId);
    if (!config) return false;

    const playerLevel = this.playerLevel.get();
    return playerLevel >= config.unlock_lvl;
  }

  /**
   * Проверить, достигнут ли максимальный уровень
   */
  isMaxLevel(upgradeId: UpgradeId): boolean {
    const config = this.getUpgradeConfig(upgradeId);
    const currentLevel = this.getUpgradeLevel(upgradeId);

    if (!config) return true;
    return currentLevel >= config.max_lvl;
  }

  /**
   * Вычислить стоимость апгрейда для следующего уровня
   */
  calculateUpgradeCost(upgradeId: UpgradeId): UpgradeCost | null {
    const config = this.getUpgradeConfig(upgradeId);
    if (!config) return null;

    const currentLevel = this.getUpgradeLevel(upgradeId);
    if (currentLevel >= config.max_lvl) return null;

    const nextLevel = currentLevel + 1;

    // Вычисляем стоимость монет
    const coinCost = this.calculateCoinCost(config, nextLevel);

    // Парсим стоимость ресурсов
    const resourceCost = this.parseResourceCost(config.base_cost_pack);

    return {
      coins: coinCost,
      resources: resourceCost,
    };
  }

  /**
   * Вычислить эффект апгрейда для заданного уровня
   */
  calculateUpgradeEffect(upgradeId: UpgradeId, level: number = -1): number {
    const config = this.getUpgradeConfig(upgradeId);
    if (!config) return 0;

    const targetLevel = level === -1 ? this.getUpgradeLevel(upgradeId) : level;
    if (targetLevel === 0) return 0;

    const effectPerLevel = config.effect_per_lvl;

    // Проверяем, является ли эффект формулой
    if (typeof effectPerLevel === 'string' && effectPerLevel.includes('=')) {
      return this.calculateFormulaEffect(effectPerLevel, targetLevel);
    }

    // Простое умножение
    const numericEffect =
      typeof effectPerLevel === 'number' ? effectPerLevel : parseFloat(effectPerLevel.toString());
    return numericEffect * targetLevel;
  }

  /**
   * Вычислить эффект по формуле
   */
  private calculateFormulaEffect(formula: string, level: number): number {
    try {
      // Обработка специальных случаев
      if (formula.includes('×')) {
        // Мультипликативный эффект типа "×2"
        const multiplier = parseFloat(formula.replace('×', ''));
        return Math.pow(multiplier, level) - 1; // Возвращаем бонус к базовому значению
      }

      if (formula.includes('%') && formula.includes('-')) {
        // Процентное снижение типа "-5%"
        const percentage = parseFloat(formula.replace(/[%-]/g, ''));
        return -(percentage / 100) * level; // Отрицательное значение для снижения
      }

      if (formula.includes('%') && formula.includes('+')) {
        // Процентное увеличение типа "+1%/card"
        const percentage = parseFloat(formula.replace(/[%+]/g, ''));
        return (percentage / 100) * level;
      }

      if (formula.includes('+') && formula.includes('cap')) {
        // Добавление капов типа "+1 cap"
        return level; // Каждый уровень добавляет 1
      }

      // Парсим формулу вида "flat=((i+1)*(i+2))/2"
      const match = formula.match(/^[^=]*=(.+)$/);
      if (!match) return 0;

      const expression = match[1];
      // Заменяем переменную i на текущий уровень
      const evaluatedExpression = expression.replace(/i/g, level.toString());

      // Безопасное вычисление выражения без eval()
      return this.safeEvaluateExpression(evaluatedExpression);
    } catch (error) {
      console.warn(`Failed to calculate formula effect: ${formula}`, error);
      return 0;
    }
  }

  /**
   * Безопасное вычисление математических выражений без eval()
   */
  private safeEvaluateExpression(expression: string): number {
    // Разрешенные символы для математических выражений
    const allowedChars = /^[0-9+\-*/().\s]+$/;
    if (!allowedChars.test(expression)) {
      throw new Error(`Invalid characters in expression: ${expression}`);
    }

    try {
      // Используем Function constructor для безопасного вычисления
      // Это безопаснее чем eval() так как создает изолированную функцию
      return new Function(`return ${expression}`)();
    } catch (error) {
      throw new Error(`Failed to evaluate expression: ${expression}`);
    }
  }

  /**
   * Вычислить стоимость монет для уровня
   */
  private calculateCoinCost(config: UpgradeConfig, level: number): number {
    if (config.cost_model !== 'coins_only' && config.cost_model !== 'coins+bars') {
      return 0;
    }

    // Формула: base_cost * growth_mult^(level-1)
    return Math.floor(config.base_cost_coin * Math.pow(config.growth_mult, level - 1));
  }

  /**
   * Парсить стоимость ресурсов из строки
   */
  private parseResourceCost(costPack: string): Array<{ itemId: string; amount: number }> {
    if (!costPack || costPack === '–') {
      return [];
    }

    const resources: Array<{ itemId: string; amount: number }> = [];

    try {
      // Формат: "3×NeonSteel + 2×FluxGlass"
      const parts = costPack.split('+').map(part => part.trim());

      for (const part of parts) {
        const match = part.match(/^(\d+)×(.+)$/);
        if (match) {
          const amount = parseInt(match[1], 10);
          const itemId = match[2];

          // Найти itemId по имени
          const item = itemsDB.getItem(itemId);
          if (item) {
            resources.push({ itemId, amount });
          }
        }
      }
    } catch (error) {
      console.warn(`Failed to parse resource cost: ${costPack}`, error);
    }

    return resources;
  }

  /**
   * Проверить, может ли игрок позволить себе апгрейд
   */
  canAffordUpgrade(upgradeId: UpgradeId): boolean {
    const cost = this.calculateUpgradeCost(upgradeId);
    if (!cost) return false;

    const currentCredits = profileActions.getInventoryItemQuantity('credits');

    // Проверяем монеты (из inventory) - используем credits
    if (currentCredits < cost.coins) {
      return false;
    }

    // Проверяем ресурсы (из inventory)
    for (const resource of cost.resources) {
      const currentAmount = profileActions.getInventoryItemQuantity(resource.itemId);
      if (currentAmount < resource.amount) {
        return false;
      }
    }

    return true;
  }

  /**
   * Получить полную информацию о покупке апгрейда
   */
  getUpgradePurchaseInfo(upgradeId: UpgradeId): UpgradePurchaseInfo | null {
    const config = this.getUpgradeConfig(upgradeId);
    if (!config) return null;

    const currentLevel = this.getUpgradeLevel(upgradeId);
    const cost = this.calculateUpgradeCost(upgradeId);
    const canAfford = this.canAffordUpgrade(upgradeId);

    return {
      upgradeId,
      currentLevel,
      newLevel: currentLevel + 1,
      cost: cost || { coins: 0, resources: [] },
      canAfford,
      isMaxLevel: this.isMaxLevel(upgradeId),
      isUnlocked: this.isUpgradeUnlocked(upgradeId),
    };
  }

  /**
   * Купить апгрейд (увеличить уровень)
   */
  purchaseUpgrade(upgradeId: UpgradeId): boolean {
    const info = this.getUpgradePurchaseInfo(upgradeId);
    if (!info || !info.canAfford || info.isMaxLevel || !info.isUnlocked) {
      return false;
    }

    // Списываем монеты (из inventory)
    profileActions.removeInventoryItem('credits', info.cost.coins);

    // Списываем ресурсы (из inventory)
    for (const resource of info.cost.resources) {
      profileActions.removeInventoryItem(resource.itemId, resource.amount);
    }

    // Увеличиваем уровень апгрейда
    profileActions.setUpgradeLevel(upgradeId, info.newLevel);

    console.log(`✅ Purchased upgrade ${upgradeId} to level ${info.newLevel}`);
    return true;
  }

  /**
   * Получить все доступные апгрейды для покупки
   */
  getAvailableUpgrades(): UpgradePurchaseInfo[] {
    const allUpgradeIds = this.ids() as UpgradeId[];
    const available: UpgradePurchaseInfo[] = [];

    for (const upgradeId of allUpgradeIds) {
      const info = this.getUpgradePurchaseInfo(upgradeId);
      if (info && info.isUnlocked && !info.isMaxLevel) {
        available.push(info);
      }
    }

    return available.sort((a, b) => a.cost.coins - b.cost.coins);
  }

  /**
   * Получить апгрейды по типу эффекта
   */
  getUpgradesByEffectType(effectType: string): UpgradeConfig[] {
    return this.all().filter(upgrade => upgrade.effect_type_stat_key === effectType);
  }

  /**
   * Получить апгрейды по уровню разблокировки
   */
  getUpgradesByUnlockLevel(unlockLevel: number): UpgradeConfig[] {
    return this.all().filter(upgrade => upgrade.unlock_lvl === unlockLevel);
  }

  /**
   * Получить все ID апгрейдов
   */
  getUpgradeIds(): UpgradeId[] {
    return this.ids() as UpgradeId[];
  }

  /**
   * Подписаться на изменения уровней апгрейдов
   */
  subscribeToUpgradeLevels(callback: (levels: Record<UpgradeId, number>) => void): () => void {
    this.setupPlayerLevelSubscription(); // Ленивая инициализация
    return profileStore.subscribe(profile => {
      callback(profile.upgrades);
    });
  }

  /**
   * Подписаться на изменения уровня игрока
   */
  subscribeToPlayerLevel(callback: (level: number) => void): () => void {
    return this.playerLevel.subscribe(callback);
  }
}

// Ленивый синглтон для удобства
let _upgradesInstance: UpgradesDB | null = null;
export function getUpgrades(): UpgradesDB {
  if (!_upgradesInstance) {
    _upgradesInstance = new UpgradesDB();
  }
  return _upgradesInstance;
}

// Синглтон для удобства
export const Upgrades = new Proxy({} as UpgradesDB, {
  get(_target, prop) {
    return getUpgrades()[prop as keyof UpgradesDB];
  },
});
